'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { IntelligenceReport } from './intelligenceData';
import { Shield, Clock, BookmarkPlus, BookmarkCheck, ArrowRight, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface ReportCardProps {
  report: IntelligenceReport;
  onSaveToggle: (id: string) => void;
}

const classificationClass: Record<string, string> = {
  Confidential: 'classification-confidential',
  Restricted: 'classification-restricted',
  General: 'classification-general',
};

export default function ReportCard({ report, onSaveToggle }: ReportCardProps) {
  const [hovered, setHovered] = useState(false);

  const ValueIcon =
    report.valueDirection === 'positive'
      ? TrendingUp
      : report.valueDirection === 'negative'
      ? TrendingDown
      : Minus;

  const valueColor =
    report.valueDirection === 'positive' ?'text-emerald-400'
      : report.valueDirection === 'negative' ?'text-red-400' :'text-muted-foreground';

  return (
    <article
      className="relative flex flex-col bg-card border border-muted rounded-sm overflow-hidden report-card-hover cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Top bar — classification */}
      <div className={`h-0.5 w-full ${
        report.classification === 'Confidential' ?'bg-red-600'
          : report.classification === 'Restricted' ?'bg-gold' :'bg-blue-500'
      }`} />

      {/* Image */}
      <div className="relative h-44 overflow-hidden">
        <AppImage
          src={report.image}
          alt={report.alt}
          fill
          className={`object-cover transition-transform duration-700 ${hovered ? 'scale-105' : 'scale-100'}`}
        />
        <div className="absolute inset-0 hero-overlay-bottom" />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className={`flex items-center gap-1 text-xs tracking-widest uppercase px-2 py-0.5 rounded-sm ${classificationClass[report.classification]}`}>
            <Shield size={10} />
            {report.classification}
          </span>
          {report.isNew && (
            <span className="exclusivity-badge text-xs tracking-widest uppercase px-2 py-0.5 rounded-sm">
              New
            </span>
          )}
        </div>

        {/* Save */}
        <button
          onClick={(e) => { e.stopPropagation(); onSaveToggle(report.id); }}
          aria-label={report.isSaved ? 'Remove from saved reports' : 'Save report'}
          className={`absolute top-3 right-3 p-2 rounded-sm transition-all duration-200 ${
            report.isSaved
              ? 'bg-gold text-obsidian' :'glass-panel text-cream hover:bg-gold hover`:text-obsidian'
          } ${hovered ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        >
          {report.isSaved ? <BookmarkCheck size={15} /> : <BookmarkPlus size={15} />}
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-5">
        {/* Segment + read time */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs tracking-widest uppercase text-gold font-medium">
            {report.segment}
          </span>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Clock size={11} />
            <span className="text-xs">{report.readTime} min</span>
          </div>
        </div>

        {/* Title */}
        <h3 className="font-editorial text-base font-semibold text-cream leading-tight mb-2">
          {report.title}
        </h3>

        {/* Subtitle */}
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-4 flex-1">
          {report.subtitle}
        </p>

        {/* Teaser on hover */}
        {hovered && (
          <p className="text-xs text-cream/60 leading-relaxed line-clamp-3 mb-4 animate-fade-in">
            {report.teaser}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-muted mt-auto">
          <div className="flex items-center gap-2">
            <ValueIcon size={13} className={valueColor} />
            <span className={`text-sm font-semibold text-tabular ${valueColor}`}>
              {report.valueImpact}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {report.analyst} · {report.publishedDate}
            </span>
            <button
              aria-label={`Read ${report.title}`}
              className="text-muted-foreground hover:text-gold transition-colors"
            >
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}